import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import { Formik } from "formik";
import { Collapse } from "react-bootstrap";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import ModuleComsaSideBarReports from "./ModuleComsaSideBarReports";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import { renderCommaFloat } from "../../../minions/int";
import Observations from "../../../layout/screenSpecifics/Modules/ComsaReports/Observations";
import Translator from "../../../layout/components/helpers/Translator";
import { toFormData } from "../../../minions/forms";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import { axiosPath } from "../../../routing/helpers/router";
import ModuleComsaWasteManagementReportManagers from "../../../layout/screenSpecifics/Modules/ComsaReports/ModuleComsaWasteManagementReportManagers";
import ModuleComsaWasteManagementAdd from "../../../layout/screenSpecifics/Modules/ComsaReports/ModuleComsaWasteManagementAdd";
import ModuleComsaWasteManagementEdit from "../../../layout/screenSpecifics/Modules/ComsaReports/ModuleComsaWasteManagementEdit";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import _ from "lodash";
import errorHandler from '@/layout/components/error/ErrorHandler';
import { getFile, handleMinDate } from '@/layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';

const ModuleComsaWasteManagementReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const currentPage = useSelector((state) => state.currentPage);
  const [loading, setLoading] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [showWasteBookModal, setShowWasteBookModal] = useState(false);
  const [reportOptions, setReportOptions] = useState([]);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    }))
  );
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [reportType] = useState("wasteManagement");
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    code: null,
    quantityFrom: null,
    quantityTo: null,
    measureUnit: null,
    wasteManagement: null,
    operation: null,
    valuablePercentageFrom: null,
    valuablePercentageTo: null,
  });
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [filterCode, setFilterCode] = useState(null);
  const [filterQuantityFrom, setFilterQuantityFrom] = useState(null);
  const [filterQuantityTo, setFilterQuantityTo] = useState(null);
  const [filterMeasureUnit, setFilterMeasureUnit] = useState(null);
  const [filterWasteManagement, setFilterWasteManagement] = useState(null);
  const [filterOperation, setFilterOperation] = useState(null);
  const [filterValuablePercentageFrom, setFilterValuablePercentageFrom] =
    useState(null);
  const [filterValuablePercentageTo, setFilterValuablePercentageTo] =
    useState(null);
  const [actualFilters, setActualFilters] = useState({
    month: null,
    year: null,
    code: null,
    quantityFrom: null,
    quantityTo: null,
    measureUnit: null,
    wasteManagement: null,
    operation: null,
    valuablePercentageFrom: null,
    valuablePercentageTo: null,
    comment: ''
  });
  const [codes, setCodes] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [wasteBooks, setWasteBooks] = useState([]);
  const [moduleData, setModuleData] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) setModuleData(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);
  // > LOAD MODULE DATA //

  // > LOAD MANAGERS
  const loadManagers = useCallback(async () => {
    await axios
      .post(axiosPath("api_r_user_module_comsaReports_manager_list"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(
            currentPageAction.change("options", {
              managers: r.data,
            })
          );
        }
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadManagers();
  }, []);

  useEffect(() => {
    if (currentPage?.options?.managers)
      setManagerOptions(
        currentPage?.options?.managers.map((m) => ({
          value: m.id,
          label: m.name,
        }))
      );
  }, [currentPage?.options?.managers]);
  // < LOAD MANAGERS

  // > LOAD LER CODES
  const loadAllCodes = 
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaReports_getWasteManagementCodes"),
          { moduleBridgeHex, subModuleBridgeHex }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('moduleComsaWasteManagementReportCodes', data?.payload || []));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))

  useEffect(() => {
    loadAllCodes();
  }, []);

  useEffect(() => {
    if (currentPage?.moduleComsaWasteManagementReportCodes) {
      const codesList = [];
      let groupLabel;
      currentPage?.moduleComsaWasteManagementReportCodes.map((c) => {
        if (c?.isTitle) {
          groupLabel = `${c.code} ${c.name}`;
          codesList.push({
            label: groupLabel,
            isOptGroup: true,
          });
        } else {
          codesList.push({
            label: `${c.code} ${c.name}`,
            value: c.id,
            group: groupLabel,
          });
        }
      });
      setCodes(codesList);
    }
  }, [currentPage?.moduleComsaWasteManagementReportCodes]);
  // > LOAD LER CODES

  // > FILTERS
  const handleFilters = () => {
    const newFilters = {
      month: filterMonth,
      year: filterYear,
      code: filterCode,
      quantityFrom: filterQuantityFrom,
      quantityTo: filterQuantityTo,
      measureUnit: filterMeasureUnit,
      wasteManagement: filterWasteManagement,
      operation: filterOperation,
      valuablePercentageFrom: filterValuablePercentageFrom,
      valuablePercentageTo: filterValuablePercentageTo,
    };
    if (!_.isEqual(newFilters, actualFilters)) {
      setActualFilters(newFilters);
      setLoading(true);
      setFilters(newFilters);
    }
  };
  const handleRemoveFilters = () => {
    const newFilters = {
      month: null,
      year: null,
      code: null,
      quantityFrom: null,
      quantityTo: null,
      measureUnit: null,
      wasteManagement: null,
      operation: null,
      valuablePercentageFrom: null,
      valuablePercentageTo: null,
    };
    setFilterMonth(null);
    setFilterYear(null);
    setFilterCode(null);
    setFilterQuantityFrom(null);
    setFilterQuantityTo(null);
    setFilterMeasureUnit(null);
    setFilterWasteManagement(null);
    setFilterOperation(null);
    setFilterValuablePercentageFrom(null);
    setFilterValuablePercentageTo(null);
    setActualFilters(newFilters);
    setLoading(true);
    setFilters(newFilters);
  };
  // < FILTERS

  // > LOAD REPORT OPTIONS //
  const loadOptions = useCallback(async () => {
    await axios.post(
        axiosPath('api_r_user_module_comsaReports_reportOptions'),
        { type: reportType, moduleBridgeHex, subModuleBridgeHex },
      )
      .then(({ data }) => {
        if (isMounted.current) setReportOptions(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [reportType, moduleBridgeHex, subModuleBridgeHex]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);
  // > LOAD REPORT OPTIONS //

  // > LOAD REPORTS //
  const loadReports = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_comsaReports_report"), {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          filters,
        })
        .then((r) => {
          if (isMounted.current) {
            setReportList(r.data);
            setIsFiltersPanelOpen(false);
            setLoading(false);
          }
        }),
    [reportType, moduleBridgeHex, subModuleBridgeHex, plantHex, filters]
  );

  useEffect(() => {
    if (loading) {
      loadReports();
    }
  }, [loading, loadReports]);
  // < LOAD REPORTS //

  // < WASTE BOOK
  const loadWasteBooks = useCallback(async () => {
    await axios
      .post(axiosPath("api_r_user_module_comsaReports_getWasteBooks"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current && !_.isEmpty(r.data)) {
          setWasteBooks(r.data);
        }
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadWasteBooks();
  }, [loadWasteBooks]);

  const editWasteBook = async (values) => {
    await axios
      .post(
        axiosPath("api_r_user_module_comsaReports_editWasteBook"),
        toFormData({
          ...values,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((r) => {
        if (isMounted.current) {
          setShowWasteBookModal(false);
          setWasteBooks(r.data);
          setLoading(true);
        }
      });
  };

  const wasteBookValidationSchema = Yup.object().shape({
    name: Yup.string().when("files", {
      is: (files) => !_.isEmpty(files),
      then: Yup.string()
        .nullable()
        .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />))
        .typeError(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    }),
  });

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        "deletedFiles",
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue("deletedFiles", [...deletedFiles, salt]);
  };
  // < WASTE BOOK

  const getCodeType = () => {
    let code;
    switch (moduleData.wasteManagementCode) {
      case "basilea":
        code = "Basilea";
        break;
      case "cretib":
        code = "CRETIB";
        break;
      default:
        code = ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="ler" />
        );
        break;
    }

    return code;
  };

  const getWasteBookLink = (managerID, salt) => axios
      .post(
          axiosPath('api_r_user_module_comsaReports_getWasteBookLink'),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            managerID,
            salt,
          }
      )
      .then(({ data }) => (window.location = data))
      .catch((e) => dispatch(errorHandler(e)))

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="waste.management" />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_waste_management"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="waste.management" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <ModuleComsaWasteManagementAdd
              reportOptions={reportOptions}
              reportList={reportList}
              codes={codes}
              managerOptions={managerOptions}
              onAdd={() => setLoading(true)}
              moduleData={moduleData}
            />
            <span
              className={`btn btn-sm mr-2 btn-${
                _.isEmpty(wasteBooks) ? 'outline-' : ''
              }info`}
              onClick={() => setShowWasteBookModal(true)}
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaReports" name="waste.book" />,
              )}
            >
              <AppRemixIcon icon="book" />
            </span>
            <ModuleComsaWasteManagementReportManagers
              reportOptions={reportOptions}
              codeType={getCodeType()}
              codes={codes}
              onUpdateReports={() => setLoading(true)}
              onUpdateManagers={loadManagers}
            />
            <span
              className={`btn btn-sm btn-${
                isFiltersPanelOpen ? '' : 'outline-'
              }info float-right`}
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
            >
              <Translator file="module-comsaReports" name="filters" />
            </span>
            {(filters.month ||
              filters.year ||
              filters.code ||
              filters.quantityFrom ||
              filters.quantityTo ||
              filters.measureUnit ||
              filters.wasteManagement ||
              filters.operation ||
              filters.valuablePercentageFrom ||
              filters.valuablePercentageTo) && (
              <span
                className="btn btn-danger btn-sm mr-1 float-right"
                onClick={() => handleRemoveFilters()}
              >
                <AppRemixIcon icon="delete-bin-6" />
              </span>
            )}
          </div>
        </div>
        <Observations reportType={reportType} />
        <div className="row mt-3">
          <div className="col-12">
            <Collapse in={isFiltersPanelOpen}>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={filterYear}
                        onChange={(e) => setFilterYear(e)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={months}
                        onChange={(e) => setFilterMonth(e)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={filterMonth}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        icon={<AppRemixIcon icon="barcode" />}
                        options={codes}
                        title={ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="code" />,
                        )}
                        value={filterCode || null}
                        onChange={setFilterCode}
                        isLoading={!codes}
                        hasSearchBox
                        hasSelectOptions
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="quantity" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />,
                        )})`}
                        icon={<AppRemixIcon icon="scales-3" />}
                        value={filterQuantityFrom || ''}
                        onChange={(number) => setFilterQuantityFrom(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="quantity" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />,
                        )})`}
                        icon={<AppRemixIcon icon="scales-3" />}
                        value={filterQuantityTo || ''}
                        onChange={(number) => setFilterQuantityTo(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="valuable.material.percentage"
                          />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />,
                        )})`}
                        icon={<AppRemixIcon icon="percent" />}
                        value={filterValuablePercentageFrom || ''}
                        onChange={(number) => setFilterValuablePercentageFrom(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="valuable.material.percentage"
                          />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />,
                        )})`}
                        icon={<AppRemixIcon icon="percent" />}
                        value={filterValuablePercentageTo || ''}
                        onChange={(number) => setFilterValuablePercentageTo(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={
                          !_.isEmpty(reportOptions) && reportOptions.measureUnit
                        }
                        onChange={(e) => setFilterMeasureUnit(e)}
                        title={
                          <Translator
                            file="module-comsaReports"
                            name="measure.unit"
                          />
                        }
                        icon={<AppRemixIcon icon="ruler-2" />}
                        placeholder={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="all.measure.units"
                          />,
                        )}
                        value={filterMeasureUnit}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={
                          !_.isEmpty(reportOptions) && reportOptions.wasteManagement
                        }
                        onChange={(e) => setFilterWasteManagement(e)}
                        title={
                          <Translator
                            file="module-comsaReports"
                            name="waste.management.single"
                          />
                        }
                        icon={<AppRemixIcon icon="delete-bin-6" />}
                        placeholder={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="all.waste.managements"
                          />,
                        )}
                        value={filterWasteManagement}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={
                          !_.isEmpty(reportOptions) &&
                          _.concat(
                            reportOptions.eliminationOperation,
                            reportOptions.valuationOperation,
                          )
                        }
                        onChange={(e) => setFilterOperation(e)}
                        title={
                          <Translator file="module-comsaReports" name="operation" />
                        }
                        icon={<AppRemixIcon icon="arrow-left-right" />}
                        placeholder={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="all.operations"
                          />,
                        )}
                        value={filterOperation}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="float-right">
                        <span
                          className="btn btn-info"
                          onClick={() => handleFilters()}
                        >
                          <Translator file="module-saicaReports" name="filter" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    {reportList.length > 0 ? (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>
                              <Translator file="module-comsaReports" name="year" />
                            </th>
                            <th>
                              <Translator file="module-comsaReports" name="month" />
                            </th>
                            <th>
                              <Translator file="module-comsaReports" name="code" /> (
                              {getCodeType()})
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="denomination"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="quantity"
                              />
                            </th>
                            <th>
                              <Translator file="module-comsaReports" name="manager" />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="waste.management.single"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="operation"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="valuable.material.percentage"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="intermediate.manager"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="evidence"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="m-cr-comment"
                              />
                            </th>
                            <th style={{ minWidth: '113px', width: '113px' }} />
                          </tr>
                        </thead>
                        <tbody>
                          {reportList &&
                            reportList.map((r) => (
                              <tr key={r.id}>
                                <td>{r.year}</td>
                                <td>
                                  <Translator
                                    file="user"
                                    name={`calendar.month${r.month}`}
                                  />
                                </td>
                                <td>
                                  {
                                    currentPage.moduleComsaWasteManagementReportCodes?.find(
                                      (c) => c.id === r.code,
                                    )?.code
                                  }
                                </td>
                                <td>
                                  {
                                    currentPage.moduleComsaWasteManagementReportCodes?.find(
                                      (c) => c.id === r.code,
                                    )?.name
                                  }
                                </td>
                                <td>
                                  {renderCommaFloat(r.quantity)}{' '}
                                  {
                                    reportOptions.measureUnit?.find(
                                      (o) => o.value === r.measureUnit,
                                    )?.label
                                  }
                                </td>
                                <td>{r?.managerName}</td>
                                <td>
                                  {
                                    reportOptions.wasteManagement?.find(
                                      (o) => o.value === r.wasteManagement,
                                    )?.label
                                  }
                                </td>
                                <td>
                                  {reportOptions.eliminationOperation?.find(
                                    (o) => o.value === r.operation,
                                  )?.label ||
                                    reportOptions.valuationOperation?.find(
                                      (o) => o.value === r.operation,
                                    )?.label}
                                </td>
                                <td>{renderCommaFloat(r.valuablePercentage)}</td>
                                <td>{r.intermediateManager}</td>
                                <td>
                                  {r.files &&
                                    Object.keys(r.files).map((salt, index) => (
                                      <span
                                        key={index}
                                        className="mr-1 pointer"
                                        title={r.files[salt].name}
                                        onClick={() =>
                                          getFile(
                                            moduleBridgeHex,
                                            subModuleBridgeHex,
                                            plantHex,
                                            reportType,
                                            r.id,
                                            salt,
                                            dispatch,
                                          )
                                        }
                                      >
                                        <AppRemixIcon icon="file-text" />{' '}
                                        {r.files[salt].name}
                                      </span>
                                    ))}
                                </td>
                                <td>{r.comment}</td>
                                <td className="text-center">
                                  <ModuleComsaWasteManagementEdit
                                    report={r}
                                    reportOptions={reportOptions}
                                    codes={codes}
                                    managerOptions={managerOptions}
                                    onEdit={() => setLoading(true)}
                                  />
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator file="module-saicaReports" name="no.results" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <AppModal
          show={showWasteBookModal}
          title={<Translator file="module-comsaReports" name="waste.book" />}
          onClose={() => setShowWasteBookModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={{
              name: '',
              files: null,
              deletedFiles: [],
              comment: '',
            }}
            validationSchema={wasteBookValidationSchema}
            onSubmit={editWasteBook}
          >
            {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={values.name}
                        onChange={(opt) => setFieldValue('name', opt)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                        popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                        error={errors.name}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label>
                      <Translator file="module-comsaReports" name="attach" />
                    </label>
                    <AppFileUploader
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator file="general" name="select.a.file" />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator file="general" name="one.file.selected" />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                    <div className="mb-3">
                      {!_.isEmpty(wasteBooks) && (
                        <div className="row">
                          <div className="col-12">
                            <label>
                              <Translator file="module-comsaReports" name="delete" />
                            </label>
                            {wasteBooks.map((wb, index) => (
                              <div key={index} className="mb-1">
                                <label className="mb-2 pointer font-weight-light">
                                  <input
                                    type="checkbox"
                                    className="mr-1"
                                    value={wb.files.salt}
                                    onChange={() =>
                                      handleDeletedFiles(
                                        setFieldValue,
                                        values.deletedFiles,
                                        wb.files.salt,
                                      )
                                    }
                                    checked={
                                      values.deletedFiles &&
                                      values.deletedFiles[wb.files.salt]
                                    }
                                  />
                                  {wb.files.name}.{wb.files.extension}
                                </label>
                                <span
                                  className="btn btn-sm btn-outline-info float-right pointer"
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="download.waste.book"
                                    />,
                                  )}
                                  onClick={() =>
                                    getWasteBookLink(wb.id, wb.files.salt)
                                  }
                                >
                                  <AppRemixIcon icon="download" />
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-success btn-block"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={
                        isSubmitting ||
                        ((values?.name?.length === 0 || values?.files === null) &&
                          values?.deletedFiles?.length === 0)
                      }
                    >
                      <Translator file="module-comsaReports" name="edit" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleComsaWasteManagementReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
